import { queryOptions } from '@tanstack/react-query';

import { queryKeys } from '@/hooks/api';

import type { User } from './fetch-user';
import type { Profile } from './get-profile';
import type { PublicEnv } from './get-public-env';
import type { SessionState } from './get-session-from-redis';

import {
  dashboardFromSession,
  type ActiveDashboard,
} from './dashboard-from-session';
import { getProfile } from './get-profile';
import { getPublicEnv } from './get-public-env';
import { getServerSession } from './get-server-session';

export type AdminUser = NonNullable<SessionState['adminUser']>;

/**
 * Complete auth context including session, user, and profile.
 */
export interface AuthContext {
  user: User | null;
  profile: Profile | null;
  isAdminImpersonating: boolean;
  adminUser: AdminUser | null;
  activeDashboard: ActiveDashboard;
  v3Enabled?: boolean;
  env: PublicEnv;
}

export { dashboardFromSession };

/**
 * React Query options for the current session with profile.
 *
 * Using queryOptions with React Query caching prevents unnecessary
 * server round trips on every navigation. The session and profile
 * will be cached client-side and only refetched when needed.
 *
 * This is the single source of truth for "am I signed in?". Every route guard
 * — the `_authenticated` layouts and the signed-out-only routes (login,
 * signup, forgot-password) alike — must read the session from the router
 * context this feeds, never from its own `getServerSession()` call. Two
 * independent reads in one request can disagree when a fetchUser/getPublicEnv
 * call fails transiently, and a disagreement between a guard and its own
 * redirect target is an infinite redirect loop (ERR_TOO_MANY_REDIRECTS), not
 * an error anyone can debug.
 */
export const getCurrentSessionOptions = queryOptions({
  queryKey: queryKeys.session.current(),
  queryFn: async (): Promise<AuthContext> => {
    try {
      const [session, publicEnv] = await Promise.all([
        getServerSession(),
        getPublicEnv(),
      ]);
      if (!session)
        return {
          user: null,
          profile: null,
          isAdminImpersonating: false,
          adminUser: null,
          activeDashboard: { organization: null, team: null },
          v3Enabled: undefined,
          env: publicEnv,
        };

      const isResearcher = session.user.roles.includes('researcher');
      const profile = isResearcher ? null : await getProfile(session.user.id);
      const rawAdminUser = session.sessionState?.adminUser ?? null;
      const adminUser = rawAdminUser?.id ? rawAdminUser : null;
      return {
        user: session.user,
        profile,
        isAdminImpersonating: !!adminUser,
        adminUser,
        activeDashboard: dashboardFromSession(session),
        v3Enabled: session.v3Enabled,
        env: publicEnv,
      };
    } catch (error) {
      // Degrading to a signed-out context rather than rethrowing is deliberate:
      // this query is awaited in the root route's `beforeLoad`, so a rejection
      // would 500 every page of the site including the ones that don't need a
      // session at all. The failure mode of the fallback is bounded because it
      // is shared: every guard sees the same `user: null` and agrees the
      // visitor is signed out, so the worst case is being asked to log in
      // again, never a redirect ping-pong. Do not "fix" this by having a route
      // second-guess the fallback with its own session call.
      console.error('Session fetch failed:', error);
      return {
        user: null,
        profile: null,
        isAdminImpersonating: false,
        adminUser: null,
        activeDashboard: { organization: null, team: null },
        v3Enabled: undefined,
        env: {
          WEBSITE_URL: '',
          FACEBOOK_APPID: '',
          GOOGLE_BROWSER_APIKEY: '',
          PUSHER_APP_KEY: '',
          POSTHOG_API_KEY: '',
          INTERCOM_APP_ID: '',
          GOOGLE_ANALYTICS_MEASUREMENT_ID: '',
          GOOGLE_TAG_MANAGER_CONTAINER_ID: '',
          SEGMENT_WRITE_KEY: '',
          FIRST_PARTY_ANALYTICS_WRITE_KEY: '',
          FIRST_PARTY_ANALYTICS_HOST: '',
          ENVIRONMENT: 'local',
          RECAPTCHA_V3_SITE_KEY: '',
          RECAPTCHA_V2_SITE_KEY: '',
        },
      };
    }
  },
  staleTime: 0, // Always revalidate on mount/focus
  gcTime: 1000 * 60 * 5, // Keep cached for 5 min for instant navigation
  refetchOnMount: 'always',
  refetchOnWindowFocus: true,
  refetchOnReconnect: true,
  retry: false, // Don't retry failed auth checks
});
